import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/** Where a link in the file should go: an in-app route (`to`) or a plain address (`href`). */
export type RepoMarkdownLink = { to?: string; href?: string };

/**
 * A Markdown file from the repository, shown as it is in the current design
 * language. The file stays the single source; the page adds nothing to it.
 */
export const RepoMarkdown: React.FC<{
  children: string;
  /** Maps a link in the file to a route or address. Defaults to the address as written. */
  resolveLink?: (href: string | undefined) => RepoMarkdownLink;
}> = ({ children, resolveLink = (href): RepoMarkdownLink => ({ href }) }) => (
  <div className="text-[15px] leading-relaxed text-[var(--text-sec)]">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: (p) => <h2 className="mb-4 mt-8 font-serif text-[26px] leading-snug first:mt-0" style={{ color: 'var(--text-pri)' }}>{p.children}</h2>,
        h2: (p) => <h3 className="mb-3 mt-10 border-t pt-6 font-serif text-[21px] leading-snug" style={{ color: 'var(--text-pri)', borderColor: 'var(--rule)' }}>{p.children}</h3>,
        h3: (p) => <h4 className="mb-2 mt-7 font-heading text-[16px] font-semibold" style={{ color: 'var(--text-pri)' }}>{p.children}</h4>,
        h4: (p) => <h5 className="mb-2 mt-5 font-heading text-[15px] font-semibold" style={{ color: 'var(--text-pri)' }}>{p.children}</h5>,
        p: (p) => <p className="my-3">{p.children}</p>,
        ul: (p) => <ul className="my-3 list-disc space-y-1.5 pl-6">{p.children}</ul>,
        ol: (p) => <ol className="my-3 list-decimal space-y-1.5 pl-6">{p.children}</ol>,
        strong: (p) => <strong className="font-semibold text-[var(--text-pri)]">{p.children}</strong>,
        blockquote: (p) => (
          <blockquote className="my-5 border-l-[3px] bg-[var(--surface)] px-5 py-3" style={{ borderLeftColor: 'var(--mustard)' }}>
            {p.children}
          </blockquote>
        ),
        hr: () => <hr className="my-8" style={{ borderColor: 'var(--rule)' }} />,
        a: (p) => {
          const t = resolveLink(p.href);
          const cls = 'text-[var(--p-gold)] underline underline-offset-4';
          return t.to ? (
            <Link to={t.to} className={cls}>{p.children}</Link>
          ) : (
            <a href={t.href} className={cls} target="_blank" rel="noopener noreferrer">{p.children}</a>
          );
        },
        pre: (p) => (
          <pre className="my-4 overflow-x-auto border bg-[var(--surface)] p-4 font-mono text-[13px] leading-relaxed text-[var(--text-pri)]" style={{ borderColor: 'var(--rule)' }}>
            {p.children}
          </pre>
        ),
        code: (p) => <code className="font-mono text-[0.9em] text-[var(--text-pri)]">{p.children}</code>,
        table: (p) => (
          <div className="my-5 overflow-x-auto">
            <table className="w-full border-collapse text-left text-[14px]">{p.children}</table>
          </div>
        ),
        th: (p) => <th className="border-b px-3 py-2 font-mono text-[13px] font-medium uppercase tracking-[0.12em] text-[var(--text-ter)]" style={{ borderColor: 'var(--rule-bright)' }}>{p.children}</th>,
        td: (p) => <td className="border-b px-3 py-2 align-top" style={{ borderColor: 'var(--rule)' }}>{p.children}</td>,
      }}
    >
      {children}
    </ReactMarkdown>
  </div>
);
